/**
 * Small made from Large (Phase 86 Round 4, R4-3): the commit's derive mode,
 * for the whole Product sync page.
 *
 * After a `<platen>:Large` cell publishes, the By colour grid asks this hook
 * to make the same colour's `<platen>:Small` file from it. The hook calls the
 * SAME `productSync.commitVariantArtworkUpload` with `deriveSmallFrom` (no
 * mint, no PUT, no bytes from the browser): the server checks the source,
 * makes the Small file and publishes it with its made marker, or keeps a
 * Small file someone uploaded (R4-4).
 *
 * - The page holds ONE queue (`DeriveSmallContext`, beside useStagedDesigns),
 *   so collapsing a product row never drops a queued make or its answer, and
 *   a multi-colour upload never asks the server's encode slot for several
 *   files at once. A grid outside the page falls back to its own queue.
 * - State per (product, colour row, platen, side): `queued` (waiting its
 *   turn), `making`, then `made`, `kept` (the SKUs that hold their own Small
 *   and how many still need a file) or `failed` (translated, with `retry`).
 * - Each job is tagged: a finished job never overwrites a newer job's state
 *   for the same cell, and a newer job replaces one still waiting.
 * - When a job starts, Small by hand is checked again: a product (or one
 *   colour of it) switched to by hand while its job waited is never made.
 * - The job's `refresh` (the file state and URL refetch) is awaited before
 *   made or kept shows, so the locked box stays until the cell can show it.
 *
 * Every state write is dropped after unmount.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DtgPlatenCode } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import type { ArtworkSide } from './useVariantArtworkUpload';
import { uploadRefusalKey } from '../lib/upload-refusal';

/**
 * The most targets one derive call takes: the server's MAX_COMMIT_SKUS (64,
 * as `MAX_GROUP_COMMIT_SKUS`) less the source SKU, which needs the 64th read slot.
 */
export const MAX_DERIVE_TARGET_SKUS = 63;

export type DeriveSmallPhase = 'idle' | 'queued' | 'making' | 'made' | 'kept' | 'failed';

export interface DeriveSmallCellState {
  phase: DeriveSmallPhase;
  /** `kept`: how many of the job's own SKUs hold their own Small file (0: another product's file under the same name). */
  keptCount?: number;
  /** `kept`: how many of the job's SKUs still need a Small file. */
  restCount?: number;
  /** `failed`: the translated reason. */
  message?: string;
}

/** One derive: make `platen`'s Small for `skus` (one colour row and side) from `sourceSku`'s Large. */
export interface DeriveSmallJob {
  productId: string;
  rowKey: string;
  platen: DtgPlatenCode;
  side: ArtworkSide;
  locationId: string;
  /** The colour row's `<platen>:Small` SKUs. */
  skus: string[];
  /** A SKU of the same colour whose `<platen>:Large` side is published. */
  sourceSku: string;
  /** Refetch the file state and URLs; awaited before made or kept shows. */
  refresh?: () => Promise<unknown> | void;
}

export interface DeriveSmallApi {
  stateFor: (productId: string, rowKey: string, platen: DtgPlatenCode, side: ArtworkSide) => DeriveSmallCellState;
  enqueue: (job: DeriveSmallJob) => void;
  /** Run the cell's last job again (after `failed`). */
  retry: (productId: string, rowKey: string, platen: DtgPlatenCode, side: ArtworkSide) => void;
}

const IDLE: DeriveSmallCellState = { phase: 'idle' };

/**
 * Derive refusal cause codes (the server's reasons, FORWARDABLE_CAUSE_CODES)
 * to their i18n keys. Any other error shows the plain fallback, never the
 * server's text.
 */
const DERIVE_REFUSAL_KEYS: Readonly<Record<string, string>> = {
  platen_derive_other_product: 'platen.derive.refusal.otherProduct',
  platen_derive_source_not_large: 'platen.derive.refusal.sourceNotLarge',
  platen_derive_no_preset: 'platen.derive.refusal.noPreset',
  platen_derive_source_missing: 'platen.derive.refusal.sourceMissing',
  platen_derive_source_changed: 'platen.derive.refusal.sourceChanged',
  platen_derive_busy: 'platen.derive.refusal.busy',
  platen_derive_failed: 'platen.derive.refusal.failed',
};

/** The i18n key for a derive refusal: a derive cause code, an upload refusal, or the fallback. */
export function deriveRefusalKey(err: unknown): string {
  const code = (err as { data?: { cause?: { code?: unknown } } } | null | undefined)?.data?.cause?.code;
  if (typeof code === 'string' && DERIVE_REFUSAL_KEYS[code]) return DERIVE_REFUSAL_KEYS[code];
  return uploadRefusalKey(err) ?? 'platen.derive.failedFallback';
}

export function deriveCellKey(productId: string, rowKey: string, platen: DtgPlatenCode, side: ArtworkSide): string {
  return `${productId}|${rowKey}|${platen}|${side}`;
}

const jobKey = (job: DeriveSmallJob): string => deriveCellKey(job.productId, job.rowKey, job.platen, job.side);

function chunk(skus: readonly string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < skus.length; i += size) out.push(skus.slice(i, i + size));
  return out;
}

interface TaggedJob {
  id: number;
  job: DeriveSmallJob;
}

export function useDeriveSmallPlatenArtwork({
  isSmallByHand,
}: {
  /** The page's Small by hand choice (platen-wide, or this colour's), read again when a job starts. */
  isSmallByHand?: (productId: string, platen: DtgPlatenCode, rowKey: string) => boolean;
} = {}): DeriveSmallApi {
  const { t } = useTranslation('productSync');
  const commit = trpc.productSync.commitVariantArtworkUpload.useMutation();
  const commitRef = useRef(commit.mutateAsync);
  commitRef.current = commit.mutateAsync;
  const byHandRef = useRef(isSmallByHand);
  byHandRef.current = isSmallByHand;
  const tRef = useRef(t);
  tRef.current = t;

  const [states, setStates] = useState<Record<string, DeriveSmallCellState>>({});
  const mountedRef = useRef(true);
  const queueRef = useRef<TaggedJob[]>([]);
  const runningRef = useRef(false);
  const nextIdRef = useRef(0);
  /** The newest job id per cell: only that job may write the cell's state. */
  const ownerRef = useRef(new Map<string, number>());
  const lastJobRef = useRef(new Map<string, DeriveSmallJob>());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current = [];
    };
  }, []);

  const setCell = useCallback((key: string, id: number, state: DeriveSmallCellState) => {
    if (!mountedRef.current || ownerRef.current.get(key) !== id) return;
    setStates((prev) => ({ ...prev, [key]: state }));
  }, []);

  const runJob = useCallback(
    async ({ id, job }: TaggedJob): Promise<void> => {
      const key = jobKey(job);
      if (ownerRef.current.get(key) !== id) return;
      // A product switched to by hand while this job waited is never made.
      if (byHandRef.current?.(job.productId, job.platen, job.rowKey)) {
        setCell(key, id, IDLE);
        return;
      }
      setCell(key, id, { phase: 'making' });
      let kept = false;
      let keptCount = 0;
      let restCount = 0;
      let next: DeriveSmallCellState;
      try {
        for (const group of chunk(job.skus, MAX_DERIVE_TARGET_SKUS)) {
          const result = await commitRef.current({
            locationId: job.locationId,
            sku: group[0]!,
            skus: group,
            side: job.side,
            expectedPlatenVariantKey: `${job.platen}:Small`,
            deriveSmallFrom: { sourceSku: job.sourceSku },
          });
          // A kept chunk wrote nothing: its other SKUs still need a file. The
          // kept list can also name another product's SKU that serves the same
          // Small file name (review B1), so only this chunk's own are taken off.
          const keptSkus = result.derived?.kept ?? [];
          if (keptSkus.length > 0) {
            // Review round 3: count only this chunk's own SKUs as kept.
            const own = keptSkus.filter((sku) => group.includes(sku)).length;
            kept = true;
            keptCount += own;
            restCount += Math.max(0, group.length - own);
          }
        }
        next = kept ? { phase: 'kept', keptCount, restCount } : { phase: 'made' };
      } catch (err) {
        next = { phase: 'failed', message: tRef.current(deriveRefusalKey(err)) };
      }
      // A refused later chunk may follow published ones: refetch either way,
      // and keep the locked box until the cell can show what landed.
      if (mountedRef.current) {
        try {
          await job.refresh?.();
        } catch {
          // The cell's own read shows its error; the derive answer still stands.
        }
      }
      setCell(key, id, next);
    },
    [setCell],
  );

  const pump = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      while (mountedRef.current && queueRef.current.length > 0) {
        await runJob(queueRef.current.shift()!);
      }
    } finally {
      runningRef.current = false;
    }
  }, [runJob]);

  const enqueue = useCallback(
    (job: DeriveSmallJob) => {
      if (job.skus.length === 0) return;
      const key = jobKey(job);
      const id = ++nextIdRef.current;
      ownerRef.current.set(key, id);
      lastJobRef.current.set(key, job);
      // A newer job for the same cell replaces one still waiting.
      queueRef.current = queueRef.current.filter((q) => jobKey(q.job) !== key);
      queueRef.current.push({ id, job });
      setCell(key, id, { phase: 'queued' });
      void pump();
    },
    [pump, setCell],
  );

  const retry = useCallback(
    (productId: string, rowKey: string, platen: DtgPlatenCode, side: ArtworkSide) => {
      const job = lastJobRef.current.get(deriveCellKey(productId, rowKey, platen, side));
      if (job) enqueue(job);
    },
    [enqueue],
  );

  const stateFor = useCallback(
    (productId: string, rowKey: string, platen: DtgPlatenCode, side: ArtworkSide) =>
      states[deriveCellKey(productId, rowKey, platen, side)] ?? IDLE,
    [states],
  );

  return useMemo(() => ({ stateFor, enqueue, retry }), [stateFor, enqueue, retry]);
}

/** The page's one derive queue (ProductSyncPage provides it beside useStagedDesigns). */
export const DeriveSmallContext = createContext<DeriveSmallApi | null>(null);

/** The page's derive queue, or this caller's own when it renders outside the page. */
export function usePageDeriveSmall(): DeriveSmallApi {
  const shared = useContext(DeriveSmallContext);
  const local = useDeriveSmallPlatenArtwork();
  return shared ?? local;
}
